import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useCashDroPaymentState from "../atoms/useCashDroPaymentAtom";
import { useSetPriorityModal } from "../atoms/usePriorityModal";
import {
  CashDroFailure,
  CashDroOperationResult,
  CashDroOperationType,
  describeRequestError,
  getCashDroBridge,
  getCashDroErrorKey,
  logCashDro,
  toCashDroField,
} from "../utils/cashDro";
import { formatPrice } from "../utils/helpers";
import useAccountQuery from "./useAccountQuery";
import useCashDroConfigHook from "./useCashDroConfigHook";

export interface CashDroCollectRequest {
  amount: number;
  /** Our own reference for the operation, shown in CashDro's movements (e.g. "S123"). */
  aliasId?: string;
  onPaid: () => Promise<unknown>;
}

export interface CashDroRefundRequest {
  amount: number;
  aliasId?: string;
  /** Called with what the terminal actually paid out. */
  onRefunded: (refundedAmount: number, operationId: string) => Promise<unknown>;
}

const LABELS = {
  [CashDroOperationType.SALE]: "payment",
  [CashDroOperationType.PAYMENT]: "refund",
};

/**
 * Moves cash through the CashDro:
 *  - collect: takes a payment from the customer; the order / session / ledger is only saved once
 *    the terminal reports the money as received, so a cancelled or failed payment is never booked.
 *  - refund: pays money back to the customer, e.g. for a cancelled cash order.
 */
const useCashDroPaymentHook = () => {
  const { t } = useTranslation();
  const { isActive } = useCashDroConfigHook();
  const { data: account } = useAccountQuery();
  const setPriorityModal = useSetPriorityModal();
  const [state, setState] = useCashDroPaymentState();

  const describeFailure = (failure: CashDroFailure) => {
    const key = getCashDroErrorKey(failure);
    return key ? t(key) : failure.message;
  };

  const finish = () => {
    setPriorityModal("", {});
    setState(null);
  };

  const close = () => {
    logCashDro("modal closed", {
      phase: state?.phase,
      operationId: state?.operationId,
    });
    finish();
  };

  const save = async (onPaid: () => Promise<unknown>, operationId: string) => {
    logCashDro("saving", { operationId });
    setState((prev) => prev && { ...prev, phase: "saving", error: undefined });
    try {
      await onPaid();
    } catch (error) {
      logCashDro("saving failed, the money has already moved", {
        operationId,
        error: describeRequestError(error),
      });
      setState((prev) => prev && { ...prev, phase: "saveFailed" });
      return;
    }
    logCashDro("saved", { operationId });

    // Until this call the operation stays in CashDro's "pending import" list, which is where
    // money that moved but was never booked can be found again.
    getCashDroBridge()
      ?.markImported(operationId)
      .then((result) =>
        logCashDro("marked as imported", { operationId, result })
      );
    finish();
  };

  const run = async (
    type: CashDroOperationType,
    amount: number,
    aliasId: string | undefined,
    book: (bookedAmount: number, operationId: string) => Promise<unknown>
  ) => {
    const label = LABELS[type];
    const bridge = getCashDroBridge();
    const request = {
      type,
      amount,
      posUser: toCashDroField(account?.name),
      aliasId: toCashDroField(aliasId),
    };
    logCashDro(`${label}: starting`, request);
    setState({
      type,
      phase: "starting",
      amount,
      totalIn: 0,
      totalOut: 0,
      onPaid: () => Promise.resolve(),
    });
    setPriorityModal("cashDroPayment");

    const unsubscribe = bridge.onStatus((status) => {
      setState(
        (prev) =>
          prev && {
            ...prev,
            phase: prev.phase === "cancelling" ? prev.phase : "collecting",
            operationId: status.operationId,
            totalIn: status.totalIn ?? prev.totalIn,
            totalOut: status.totalOut ?? prev.totalOut,
          }
      );
    });

    let result: CashDroOperationResult;
    try {
      result = await bridge.startOperation(request);
    } catch (error) {
      result = { ok: false, message: String(error) };
    } finally {
      unsubscribe();
    }
    logCashDro(`${label}: terminal finished`, result);

    if (result.ok === false) {
      const failure = result;
      const shownMessage = describeFailure(failure);
      logCashDro(`${label}: failed, nothing booked`, {
        ...failure,
        shownMessage,
      });
      setState(
        (prev) =>
          prev && {
            ...prev,
            phase: "failed",
            operationId: failure.operationId ?? prev.operationId,
            error: shownMessage,
          }
      );
      return;
    }

    const { operationId, outcome, totalIn, totalOut, changeNotAvailable } =
      result;
    if (outcome === "cancelled") {
      logCashDro(`${label}: cancelled, nothing booked`, { operationId });
      finish();
      toast.info(
        t(
          type === CashDroOperationType.PAYMENT
            ? "cashDro.refundCancelled"
            : "cashDro.paymentCancelled"
        )
      );
      return;
    }

    let bookedAmount = amount;
    if (outcome === "partial") {
      if (type === CashDroOperationType.SALE) {
        logCashDro(
          "payment: partial, nothing booked - needs manual follow-up",
          {
            operationId,
            amount,
            totalIn,
            totalOut,
          }
        );
        setState(
          (prev) =>
            prev && {
              ...prev,
              operationId,
              totalIn,
              totalOut,
              phase: "partial",
            }
        );
        return;
      }
      // Money did leave the terminal, so what was paid out is booked; the rest is still owed.
      bookedAmount = Math.round(totalOut - totalIn) / 100;
      logCashDro("refund: partial, booking what was paid out", {
        operationId,
        amount,
        bookedAmount,
      });
      toast.warning(
        t("cashDro.refundPartial", {
          paidOut: formatPrice(bookedAmount),
          amount: formatPrice(amount),
          remaining: formatPrice(amount - bookedAmount),
        }),
        { autoClose: false }
      );
    }

    if (changeNotAvailable > 0) {
      logCashDro(`${label}: terminal could not return all change`, {
        operationId,
        changeNotAvailable,
      });
      toast.warning(
        t("cashDro.changeNotAvailable", {
          amount: formatPrice(changeNotAvailable / 100),
        }),
        { autoClose: false }
      );
    }

    const onPaid = () => book(bookedAmount, operationId);
    setState(
      (prev) => prev && { ...prev, operationId, totalIn, totalOut, onPaid }
    );
    await save(onPaid, operationId);
  };

  const collect = ({ amount, aliasId, onPaid }: CashDroCollectRequest) =>
    run(CashDroOperationType.SALE, amount, aliasId, () => onPaid());

  const refund = ({ amount, aliasId, onRefunded }: CashDroRefundRequest) =>
    run(CashDroOperationType.PAYMENT, amount, aliasId, onRefunded);

  const cancel = async () => {
    logCashDro("cancel requested by the cashier", {
      operationId: state?.operationId,
    });
    setState((prev) => prev && { ...prev, phase: "cancelling" });
    const result = await getCashDroBridge().cancelOperation();
    logCashDro("cancel answer", result);
    if (result.ok === false) {
      const failure = result;
      // The operation may have completed meanwhile; only fall back if we are still cancelling.
      setState(
        (prev) =>
          prev && {
            ...prev,
            phase: prev.phase === "cancelling" ? "collecting" : prev.phase,
          }
      );
      toast.error(describeFailure(failure));
    }
  };

  const retrySave = () => {
    if (state) {
      logCashDro("retrying to save", { operationId: state.operationId });
      save(state.onPaid, state.operationId);
    }
  };

  return { isActive, state, collect, refund, cancel, retrySave, close };
};

export default useCashDroPaymentHook;
